// RITA-1549 — Rückfragen zum Drucken der Vertragserstellung (Skizze 3c, freigegeben von Julia am 20.10.2026):
// Bild B „Schon ein Vertrag gedruckt“ (vor dem Fenster, Entscheidung 5), das kleine Drucken-Fenster der
// Bearbeiten-Seite mit den Häkchen (Bild C, F4) und die Warnung bei einem Entwurfskonflikt beim Druck (F14, Muster
// EntwurfKonfliktDialog: inzwischen gespeichert → „Gespeicherten Stand laden“ oder „Trotzdem drucken“; inzwischen
// verworfen → „Zurück zum Berater“ oder „Trotzdem drucken“, denn einen gespeicherten Stand gibt es nicht mehr). Die
// Knöpfe stehen in der freigegebenen Reihenfolge; der empfohlene Weg ist ausgefüllt. Die Aufrufer hängen jede
// Rückfrage nur ein, solange sie offen ist (wie EntwurfDialoge.tsx).
//
// RITA-1552 (Skizze Rücklauf, Bild F; F9, freigegeben von Julia am 22.10.2026): Rückfrage „Es fehlen Angaben“ vor jedem
// Druck aus dem Fenster (Variante `fenster`: genaue Liste der angehakten Dokumente) bzw. „Beim Anlegen fehlten Angaben“
// auf der Bearbeiten-Seite (Variante `seite`: nur, was beim Anlegen des Entwurfs leer war). Knöpfe „Abbrechen“ ·
// „Trotzdem drucken“ (ausgefüllt) wie die anderen Rückfragen aus 3c; während des Drucks gesperrt.
//
// RITA-1552 (Skizze Rücklauf, Bild D a; F5): Rückfrage „Schon eine Rücksendung gedruckt“ vor dem Fenster „Rücksendung
// drucken“ – gleich gebaut wie Bild B aus 3c (Abbrechen · Trotzdem neue erstellen), beide über dieselbe Hülle.
//
// RITA-1552 (Skizze Rücklauf, Bild E; F8): Das Drucken-Fenster der Bearbeiten-Seite gibt es für die Rücksendung ohne
// Häkchen (`ohneAuswahl`), weil sie nur ein Dokument hat: statt der Häkchen eine Zeile „✉ <Vorlagenname>“, derselbe Text
// wie in 3c, kein Hinweis zum Dateinamen; „Drucken“ liefert alle Dokumente als angehakt.
//
// RITA-1599 (Fund 34, Wortlaut mit Julia am 03.11.2026 abgestimmt): Dieselbe Rückfrage des Fensters fragt auch, wenn für
// den Vertrag (bzw. das Anschreiben Versand) keine Vorlage passt und deshalb nur das andere Dokument gedruckt würde:
// „Der Vertrag fehlt“ bzw. „Das Anschreiben fehlt“; fehlen zusätzlich Angaben, kommt beides in einer Rückfrage „Bitte
// vor dem Druck prüfen“ – zuerst der Satz zum fehlenden Dokument, darunter die bekannte Liste. Knöpfe wie bisher.
//
// RITA-1601 (Funde 63, 118, 128; Wortlaut und Lage von Julia am 04.11.2026 freigegeben): Die Rückfrage vor dem Druck
// (DruckRueckfrageDialog, früher FehlendeAngabenDialog) nimmt alle Gründe aus services/druckrueckfragen.ts: auf der
// Bearbeiten-Seite „Schon ein Vertrag gedruckt“ (ein Druck mit Vertrag ersetzt den gedruckten Vertrag; mit den Sätzen zu
// Scan und älterem Entwurf, nur wenn sie zutreffen), in Fenster und Seite „Der Vertragsinhaber fehlt im Vertrag“, dazu
// wie bisher fehlendes Dokument und fehlende Angaben. Ein Grund allein trägt seinen Titel; mehrere stehen in einer
// einzigen Rückfrage „Bitte vor dem Druck prüfen“, von oben nach unten: Ersetzen, fehlendes Dokument, Vertragsinhaber,
// Angaben. Der graue Hinweis nennt den schwersten Grund in derselben Reihenfolge (das Ersetzen lässt sich nicht
// rückgängig machen). Die Kennungen `fehlende-angaben-*` bleiben wegen der Browser-Tests. Das kleine Drucken-Fenster
// sagt beim Teildruck richtig, dass der Entwurf mit allen Dokumenten erhalten bleibt (Fund 118); sein „Drucken“ wartet
// bei angehaktem Vertrag (`druckenGesperrt` je Auswahl), bis die Seite den Druckstand frisch geladen hat, und zeigt mit
// `warnung`, wenn das nicht gelang – nur das Anschreiben druckt dann weiter wie bisher.
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import type { ContractCreationKind } from 'global-intern-types';
import type { Druckkonflikt } from '../hooks/useVertragDrucken';
import type { Druckauswahl } from '../services/druckdokumente';
import { anzahlGruende, Druckgruende } from '../services/druckrueckfragen';
import {
  dateinameHinweis,
  DRUCK_KONFLIKT_TITEL,
  DRUCK_VERWORFEN_HINWEIS,
  DRUCK_VERWORFEN_TEXT,
  DRUCK_VERWORFEN_TITEL,
  DRUCKEN,
  DRUCKEN_FENSTER_TITEL,
  druckenFensterText,
  druckKonfliktHinweis,
  druckKonfliktText,
  FEHLEND_FRAGE,
  FEHLEND_HINWEIS,
  FEHLEND_SEITE_HINWEIS,
  FEHLEND_SEITE_TITEL,
  FEHLEND_TEXT,
  FEHLEND_TITEL,
  FEHLENDES_DOKUMENT,
  fehlendSeiteText,
  SCHON_GEDRUCKT_HINWEIS,
  SCHON_GEDRUCKT_SEITE_HINWEIS,
  SCHON_GEDRUCKT_TITEL,
  schonGedrucktSeiteSatz,
  schonGedrucktText,
  SCHON_RUECKSENDUNG_HINWEIS,
  SCHON_RUECKSENDUNG_TITEL,
  schonRuecksendungText,
  TROTZDEM_DRUCKEN,
  TROTZDEM_NEU,
  TROTZDEM_NEUE,
  VERTRAGSINHABER_FEHLT_HINWEIS,
  VERTRAGSINHABER_FEHLT_TITEL,
  VOR_DEM_DRUCK_PRUEFEN_TITEL,
  WIRD_GEDRUCKT,
} from '../services/vertragsdruckTexte';
import { vertragsinhaberFehltFensterText } from '../services/vertragserstellungTexte';
import {
  ABBRECHEN,
  GESPEICHERTEN_STAND_LADEN,
  vertragsinhaberFehltSeiteText,
  ZURUECK_ZUM_BERATER,
} from '../services/vertragsentwurfTexte';

/** Knopftexte wie freigegeben schreiben; das App-Design setzt sonst jedes Wort groß. */
const KNOEPFE = { '& .MuiButton-root': { textTransform: 'none' } } as const;

const HINWEIS = { mt: 1.5, fontSize: 13, color: 'text.secondary' } as const;

/** Hülle der Rückfragen „schon gedruckt“: Titel, Text, grauer Hinweis, „Abbrechen“ · ausgefüllter Trotzdem-Knopf. */
function SchonRueckfrage({
  kennung,
  titel,
  text,
  hinweis,
  trotzdem,
  onAbbrechen,
  onTrotzdem,
}: {
  /** Vorsilbe der Kennungen (`<kennung>-dialog`, `-titel`, `-text`, `-trotzdem`). */
  kennung: string;
  titel: string;
  text: string;
  hinweis: string;
  trotzdem: string;
  onAbbrechen: () => void;
  onTrotzdem: () => void;
}) {
  return (
    <Dialog open onClose={onAbbrechen} aria-labelledby={`${kennung}-titel`} data-testid={`${kennung}-dialog`}>
      <DialogTitle id={`${kennung}-titel`}>{titel}</DialogTitle>
      <DialogContent>
        <DialogContentText data-testid={`${kennung}-text`}>{text}</DialogContentText>
        <Typography sx={HINWEIS}>{hinweis}</Typography>
      </DialogContent>
      <DialogActions sx={KNOEPFE}>
        <Button onClick={onAbbrechen}>{ABBRECHEN}</Button>
        <Button variant="contained" onClick={onTrotzdem} data-testid={`${kennung}-trotzdem`}>
          {trotzdem}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Bild B – Schon ein Vertrag gedruckt
export function SchonGedrucktDialog({
  datumIso,
  onAbbrechen,
  onTrotzdem,
}: {
  /** created_at des neuesten Druck-Eintrags mit Vertrag. */
  datumIso: string;
  onAbbrechen: () => void;
  onTrotzdem: () => void;
}) {
  return (
    <SchonRueckfrage
      kennung="schon-gedruckt"
      titel={SCHON_GEDRUCKT_TITEL}
      text={schonGedrucktText(datumIso)}
      hinweis={SCHON_GEDRUCKT_HINWEIS}
      trotzdem={TROTZDEM_NEU}
      onAbbrechen={onAbbrechen}
      onTrotzdem={onTrotzdem}
    />
  );
}

// RITA-1552 – Skizze Rücklauf, Bild D a: Schon eine Rücksendung gedruckt (nur mit Vertrag aus dem PRM, E3)
export function SchonRuecksendungDialog({
  vertragIso,
  ruecksendungIso,
  onAbbrechen,
  onTrotzdem,
}: {
  /** created_at des Vertrags, zu dem die Rücksendung gehört (neuester Druck mit Vertrag). */
  vertragIso: string;
  /** created_at der neuesten Rücksendung zu diesem Vertrag. */
  ruecksendungIso: string;
  onAbbrechen: () => void;
  onTrotzdem: () => void;
}) {
  return (
    <SchonRueckfrage
      kennung="schon-ruecksendung"
      titel={SCHON_RUECKSENDUNG_TITEL}
      text={schonRuecksendungText(vertragIso, ruecksendungIso)}
      hinweis={SCHON_RUECKSENDUNG_HINWEIS}
      trotzdem={TROTZDEM_NEUE}
      onAbbrechen={onAbbrechen}
      onTrotzdem={onTrotzdem}
    />
  );
}

export interface Druckteilwahl {
  kind: ContractCreationKind;
  /** Name der Vorlage, aus der das Dokument stammt. */
  name: string;
}

// Bild C – Drucken-Fenster der Bearbeiten-Seite (F4); RITA-1552 (Skizze Rücklauf, Bild E): ohne Häkchen für die Rücksendung
export function DruckenDialog({
  teile,
  beraterName,
  drucktag,
  laeuft,
  ohneAuswahl = false,
  druckenGesperrt,
  warnung = null,
  onAbbrechen,
  onDrucken,
}: {
  /** Die im Entwurf enthaltenen Dokumente; fehlt eine Art, steht nur die andere zur Wahl. */
  teile: Druckteilwahl[];
  /** RITA-1600 (Fund 106): Vor- und Nachname des Beraters und der heutige Tag für den Namen der Druckdatei. */
  beraterName: string;
  drucktag: Date;
  laeuft: boolean;
  /**
   * RITA-1552: keine Häkchen (Rücksendung, ein Dokument) – jedes Dokument steht als Zeile mit ✉ da und wird gedruckt;
   * kein Hinweis zum Dateinamen (die Rücksendung heißt immer „Rücksendung …“).
   */
  ohneAuswahl?: boolean;
  /**
   * RITA-1601 (Fund 128): „Drucken“ ist für die aktuelle Auswahl gesperrt, solange die Seite den Druckstand für sie noch
   * nicht frisch geladen hat (Rückfrage „Schon ein Vertrag gedruckt“ ohne Wartezeit danach – der Druck muss im Klick
   * beginnen, Tab-Zwang). Die Seite sperrt nur die Auswahl mit Vertrag; nur das Anschreiben wartet auf nichts.
   * „Abbrechen“ geht immer.
   */
  druckenGesperrt?: (auswahl: Druckauswahl) => boolean;
  /** RITA-1601: gelbe Warnung unter dem Text, etwa wenn der Druckstand nicht geladen werden konnte. */
  warnung?: string | null;
  onAbbrechen: () => void;
  onDrucken: (auswahl: Druckauswahl) => void;
}) {
  const [auswahl, setAuswahl] = useState<Druckauswahl>(() =>
    Object.fromEntries(teile.map((teil) => [teil.kind, true])),
  );
  const gewaehlt = teile.filter((teil) => auswahl[teil.kind]).map((teil) => teil.kind);
  const teilauswahl = gewaehlt.length > 0 && gewaehlt.length < teile.length;
  const hinweis = !ohneAuswahl && gewaehlt.length > 0 ? dateinameHinweis(gewaehlt, beraterName, drucktag) : null;
  return (
    <Dialog
      open
      onClose={laeuft ? undefined : onAbbrechen}
      aria-labelledby="drucken-titel"
      data-testid="drucken-dialog"
    >
      <DialogTitle id="drucken-titel">{DRUCKEN_FENSTER_TITEL}</DialogTitle>
      <DialogContent>
        {ohneAuswahl ? (
          teile.map((teil) => (
            <Box
              key={teil.kind}
              data-testid="drucken-dialog-dokument"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}
            >
              <MailOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography>{teil.name}</Typography>
            </Box>
          ))
        ) : (
          <FormGroup>
            {teile.map((teil) => (
              <FormControlLabel
                key={teil.kind}
                label={teil.name}
                control={
                  <Checkbox
                    checked={!!auswahl[teil.kind]}
                    disabled={laeuft}
                    onChange={(_, angehakt) => setAuswahl((bisher) => ({ ...bisher, [teil.kind]: angehakt }))}
                    inputProps={
                      { 'data-testid': `drucken-haekchen-${teil.kind}` } as React.InputHTMLAttributes<HTMLInputElement>
                    }
                  />
                }
              />
            ))}
          </FormGroup>
        )}
        {hinweis && (
          <Typography sx={HINWEIS} data-testid="drucken-dateiname-hinweis">
            {hinweis}
          </Typography>
        )}
        <DialogContentText sx={{ mt: 1.5 }} data-testid="drucken-dialog-text">
          {druckenFensterText(teilauswahl)}
        </DialogContentText>
        {warnung && (
          <Alert severity="warning" sx={{ mt: 1.5 }} data-testid="drucken-dialog-hinweis">
            {warnung}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={KNOEPFE}>
        <Button onClick={onAbbrechen} disabled={laeuft}>
          {ABBRECHEN}
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintOutlinedIcon />}
          onClick={() => onDrucken(auswahl)}
          disabled={laeuft || gewaehlt.length === 0 || (druckenGesperrt ? druckenGesperrt(auswahl) : false)}
          data-testid="drucken-dialog-knopf"
        >
          {laeuft ? WIRD_GEDRUCKT : DRUCKEN}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// F14 – Entwurf beim Druck inzwischen gespeichert oder verworfen (Muster Bild G)
export function DruckKonfliktDialog({
  art,
  konflikt,
  laeuft,
  onStandLaden,
  onZurueckZumBerater,
  onTrotzdemDrucken,
}: {
  art: 'konflikt' | 'verworfen';
  /** Bei `konflikt`: die fremde Speicherung; „Trotzdem drucken“ braucht deren Revision. */
  konflikt?: Druckkonflikt;
  laeuft: boolean;
  /** Nur bei `konflikt`: den fremden Stand laden (eigene Änderungen verwerfen). */
  onStandLaden: () => void;
  /** Nur bei `verworfen`: die Seite ohne Druck verlassen (eigene Änderungen verwerfen). */
  onZurueckZumBerater: () => void;
  onTrotzdemDrucken: () => void;
}) {
  const verworfen = art === 'verworfen';
  const trotzdemMoeglich = verworfen || !!konflikt?.revision;
  return (
    <Dialog open aria-labelledby="druck-konflikt-titel" data-testid="druck-konflikt-dialog">
      <DialogTitle id="druck-konflikt-titel">{verworfen ? DRUCK_VERWORFEN_TITEL : DRUCK_KONFLIKT_TITEL}</DialogTitle>
      <DialogContent>
        <DialogContentText data-testid="druck-konflikt-text">
          {verworfen || !konflikt
            ? DRUCK_VERWORFEN_TEXT
            : druckKonfliktText(konflikt.updated_by_name, konflikt.updated_at)}
        </DialogContentText>
        <Typography sx={HINWEIS}>
          {verworfen || !konflikt ? DRUCK_VERWORFEN_HINWEIS : druckKonfliktHinweis(konflikt.updated_by_name)}
        </Typography>
      </DialogContent>
      <DialogActions sx={KNOEPFE}>
        {verworfen ? (
          <Button onClick={onZurueckZumBerater} disabled={laeuft} data-testid="druck-konflikt-zurueck">
            {ZURUECK_ZUM_BERATER}
          </Button>
        ) : (
          <Button variant="contained" onClick={onStandLaden} disabled={laeuft} data-testid="druck-konflikt-stand-laden">
            {GESPEICHERTEN_STAND_LADEN}
          </Button>
        )}
        <Button
          variant={verworfen ? 'contained' : 'outlined'}
          onClick={onTrotzdemDrucken}
          disabled={laeuft || !trotzdemMoeglich}
          data-testid="druck-konflikt-trotzdem"
        >
          {laeuft ? WIRD_GEDRUCKT : TROTZDEM_DRUCKEN}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// RITA-1552 – Bild F: Rückfrage „Es fehlen Angaben“ (a, Fenster) bzw. „Beim Anlegen fehlten Angaben“ (b, Seite);
// RITA-1599 „Der Vertrag fehlt“ / „Das Anschreiben fehlt“; RITA-1601 „Schon ein Vertrag gedruckt“ (Seite) und „Der
// Vertragsinhaber fehlt im Vertrag“ – alle Gründe in einer Rückfrage (siehe Kopf).
export function DruckRueckfrageDialog({
  variante,
  gruende,
  laeuft,
  onAbbrechen,
  onTrotzdem,
}: {
  /** `fenster`: Fenster „Vertrag erstellen“ bzw. „Rücksendung drucken“ (Bild F a); `seite`: Bearbeiten-Seite (Bild F b). */
  variante: 'fenster' | 'seite';
  /**
   * Die Gründe der Rückfrage (services/druckrueckfragen.ts). `zeilen`: im Fenster je fehlender Angabe eine Zeile wie im
   * gelben Kasten („IBAN – im Vertrag (…)“), auf der Seite die Bezeichnungen der beim Anlegen leeren Angaben („Fax“,
   * „IBAN“); leer, wenn nur andere Gründe fragen. `fehlendesDokument` gilt nur im Fenster, `schonGedruckt` nur auf der
   * Seite.
   */
  gruende: Druckgruende;
  /** Ein Druck läuft: beide Knöpfe gesperrt, das Fenster lässt sich nicht schließen. */
  laeuft: boolean;
  onAbbrechen: () => void;
  /** Der Aufrufer druckt synchron im Klick (der Druck-Hook öffnet den Tab, bevor er wartet). */
  onTrotzdem: () => void;
}) {
  const seite = variante === 'seite';
  const { zeilen } = gruende;
  const fehlt = seite || !gruende.fehlendesDokument ? null : FEHLENDES_DOKUMENT[gruende.fehlendesDokument];
  const schonGedruckt = seite ? (gruende.schonGedruckt ?? null) : null;
  const vertragsinhaber = (gruende.vertragsinhaber ?? '').trim() || null;
  const mitAngaben = zeilen.length > 0;
  const mehrere =
    anzahlGruende({
      fehlendesDokument: fehlt ? gruende.fehlendesDokument : null,
      schonGedruckt,
      vertragsinhaber,
      zeilen,
    }) > 1;
  // Titel: ein Grund allein trägt seinen Titel, mehrere „Bitte vor dem Druck prüfen“ (RITA-1599, RITA-1601).
  let titel = seite ? FEHLEND_SEITE_TITEL : FEHLEND_TITEL;
  if (mehrere) titel = VOR_DEM_DRUCK_PRUEFEN_TITEL;
  else if (schonGedruckt) titel = SCHON_GEDRUCKT_TITEL;
  else if (fehlt) titel = fehlt.titel;
  else if (vertragsinhaber) titel = VERTRAGSINHABER_FEHLT_TITEL;
  // Grauer Hinweis: der schwerste Grund – Ersetzen (nicht rückgängig zu machen), fehlendes Dokument, Vertragsinhaber,
  // Angaben (wie RITA-1599 den Hinweis des fehlenden Dokuments vor dem der Angaben nennt).
  let hinweis = seite ? FEHLEND_SEITE_HINWEIS : FEHLEND_HINWEIS;
  if (schonGedruckt) hinweis = SCHON_GEDRUCKT_SEITE_HINWEIS;
  else if (fehlt) hinweis = fehlt.hinweis;
  else if (vertragsinhaber) hinweis = VERTRAGSINHABER_FEHLT_HINWEIS;
  /** Absätze vor der Liste bzw. dem Satz zu den Angaben, von oben nach unten (siehe Kopf). */
  const absaetze: Array<{ testId: string; text: string }> = [];
  if (schonGedruckt) {
    absaetze.push({
      testId: 'schon-gedruckt-seite-text',
      text: schonGedrucktSeiteSatz(schonGedruckt.datumIso, schonGedruckt.scanVorhanden, schonGedruckt.entwurfAelter),
    });
  }
  if (fehlt) absaetze.push({ testId: 'fehlendes-dokument-text', text: fehlt.text });
  if (vertragsinhaber) {
    absaetze.push({
      testId: 'vertragsinhaber-fehlt-text',
      text: seite ? vertragsinhaberFehltSeiteText(vertragsinhaber) : vertragsinhaberFehltFensterText(vertragsinhaber),
    });
  }
  const absatzAbstand = (index: number) => (index < absaetze.length - 1 || mitAngaben ? 1.5 : 0);
  return (
    <Dialog
      open
      onClose={laeuft ? undefined : onAbbrechen}
      aria-labelledby="fehlende-angaben-titel"
      data-testid="fehlende-angaben-dialog"
    >
      <DialogTitle id="fehlende-angaben-titel">{titel}</DialogTitle>
      <DialogContent>
        {absaetze.map((absatz, index) => (
          <DialogContentText key={absatz.testId} data-testid={absatz.testId} sx={{ mb: absatzAbstand(index) }}>
            {absatz.text}
          </DialogContentText>
        ))}
        {seite ? (
          mitAngaben && (
            <DialogContentText data-testid="fehlende-angaben-text">{fehlendSeiteText(zeilen)}</DialogContentText>
          )
        ) : (
          <>
            {mitAngaben && (
              <>
                <DialogContentText data-testid="fehlende-angaben-text">{FEHLEND_TEXT}</DialogContentText>
                {/* Die Liste steht neben dem Text, nicht darin: DialogContentText ist ein Absatz. */}
                <Box
                  component="ul"
                  data-testid="fehlende-angaben-liste"
                  sx={{ my: 0.5, pl: 2.5, color: 'text.secondary' }}
                >
                  {zeilen.map((zeile) => (
                    <li key={zeile}>{zeile}</li>
                  ))}
                </Box>
              </>
            )}
            <DialogContentText
              data-testid="fehlende-angaben-frage"
              sx={{ mt: !mitAngaben && absaetze.length > 0 ? 1.5 : 0 }}
            >
              {FEHLEND_FRAGE}
            </DialogContentText>
          </>
        )}
        <Typography sx={HINWEIS}>{hinweis}</Typography>
      </DialogContent>
      <DialogActions sx={KNOEPFE}>
        <Button onClick={onAbbrechen} disabled={laeuft}>
          {ABBRECHEN}
        </Button>
        <Button variant="contained" onClick={onTrotzdem} disabled={laeuft} data-testid="fehlende-angaben-trotzdem">
          {laeuft ? WIRD_GEDRUCKT : TROTZDEM_DRUCKEN}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
